import { COMPLETE_TRANSLATION_FRAGMENT } from '@/hooks/fragments/translation-fragments';
import { getDeviceTimezone, getTodayDate } from '@/utils/date';
import {
  GetHubPastOrdersData,
  GetHubPastOrdersQuery,
  GetHubPastOrdersQueryVariables,
} from '@/types';
import { getTranslationValue } from '@/utils/translations';
import { gql, NetworkStatus } from '@apollo/client';
import { HubOrderOption, toOrderOption } from './order-option';
import { useCallback, useMemo, useState } from 'react';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const GET_HUB_PAST_ORDERS_QUERY = gql`
  ${COMPLETE_TRANSLATION_FRAGMENT}

  query getHubPastOrders(
    $businessOrganizationUUID: uuid!
    $startDate: String!
    $lookbackDays: Int
    $userTimezone: String!
  ) {
    getHubPastOrders(
      business_organization_id: $businessOrganizationUUID
      start_date: $startDate
      lookback_days: $lookbackDays
      user_timezone: $userTimezone
    ) {
      next_available_date
      past_order_data {
        amount_in_micros
        chat_id
        invoice_id
        item_count_display {
          ...CompleteTranslationFields
        }
        operator_employees {
          name
          phone_number
          user_id
        }
        order_date
        order_date_display {
          ...CompleteTranslationFields
        }
        order_id
        restaurant_id
        restaurant_name
        restaurant_name_i18n {
          ...CompleteTranslationFields
        }
        supplier_id
      }
    }
  }
`;

export const DEFAULT_LOOKBACK_DAYS = 1;

export const usePastOrders = ({
  businessOrganizationUUID,
  lookbackDays = DEFAULT_LOOKBACK_DAYS,
  initialStartDate,
}: {
  businessOrganizationUUID: string;
  initialStartDate?: string;
  lookbackDays?: number;
}) => {
  const today = getTodayDate();
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  const { data, error, fetchMore, networkStatus, refetch } = useWrappedQuery<
    GetHubPastOrdersQuery,
    GetHubPastOrdersQueryVariables
  >(GET_HUB_PAST_ORDERS_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !businessOrganizationUUID,
    variables: {
      businessOrganizationUUID,
      lookbackDays,
      startDate: initialStartDate ?? today,
      userTimezone: getDeviceTimezone(),
    },
  });

  const nextAvailableDate = data?.getHubPastOrders?.next_available_date ?? null;

  const sectionedPastOrders = useMemo(() => {
    const pastOrders = data?.getHubPastOrders?.past_order_data ?? [];
    if (!pastOrders.length) {
      return [];
    }

    const groups = new Map<string, HubOrderOption[]>();

    for (const o of pastOrders) {
      const dateKey = o.order_date.split('T')[0];
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(toOrderOption(o));
    }

    return [...groups.entries()]
      .sort(([a], [b]) => (a > b ? -1 : 1))
      .map(([_, opts]) => {
        const firstOrder = opts[0].metadata!.order as GetHubPastOrdersData;
        const title = getTranslationValue(firstOrder.order_date_display);
        return {
          data: opts,
          key: title,
          metadata: { title },
        };
      });
  }, [data?.getHubPastOrders?.past_order_data]);

  const loadMore = useCallback(async () => {
    if (!nextAvailableDate) {
      return;
    }
    await fetchMore({
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getHubPastOrders?.past_order_data) {
          return prev;
        }

        const prevOrders = prev.getHubPastOrders?.past_order_data ?? [];
        const newOrders = fetchMoreResult.getHubPastOrders.past_order_data;

        const seen = new Set(prevOrders.map((o) => o.order_id).filter(Boolean));
        const filtered = newOrders.filter((o) => !seen.has(o.order_id));

        return {
          ...fetchMoreResult,
          getHubPastOrders: {
            ...fetchMoreResult.getHubPastOrders,
            past_order_data: [...prevOrders, ...filtered],
          },
        };
      },
      variables: {
        businessOrganizationUUID,
        lookbackDays,
        startDate: nextAvailableDate,
        userTimezone: getDeviceTimezone(),
      },
    });
  }, [businessOrganizationUUID, fetchMore, lookbackDays, nextAvailableDate]);

  const refresh = useCallback(
    async (manual = false) => {
      try {
        setIsManualRefresh(manual);
        await refetch();
      } finally {
        setIsManualRefresh(false);
      }
    },
    [refetch],
  );

  return {
    error,
    fetchMore: networkStatus === NetworkStatus.fetchMore,
    hasMore: Boolean(nextAvailableDate),
    loading: networkStatus === NetworkStatus.loading,
    loadMore,
    refresh,
    refreshing: isManualRefresh && networkStatus === NetworkStatus.refetch,
    sectionedPastOrders,
  };
};
